// #region Imports
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CheckoutModal.scss';
import { formatCurrency } from '../../../../shared/helpers';
// #endregion

export default function CheckoutModal({
  open = false,
  cart = [],
  total = 0,
  clientes = [],
  funcionarios = [],
  onClose = () => {},
  onConfirm = () => {},
}) {
  // #region Refs
  const overlayRef = useRef(null);
  const firstRef = useRef(null);
  // #endregion

  // #region Estado
  const [form, setForm] = useState({
    cliente_id: '',
    funcionario_id: '',
    metodo_pagamento: 'DINHEIRO',
    is_fiado: false,
    observacoes: '',
  });
  // #endregion

  // #region Efeitos
  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, cliente_id: '', funcionario_id: '' }));
      setTimeout(() => firstRef.current && firstRef.current.focus(), 50);
    }
  }, [open]);

  const onKeyDown = useCallback(
    e => { if (e.key === 'Escape') onClose(); },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onKeyDown]);
  // #endregion

  // #region Early return
  if (!open) return null;
  // #endregion

  // #region Handlers
  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.cliente_id || !form.funcionario_id) {
      alert('Selecione cliente e funcionário');
      return;
    }
    onConfirm(form);
  }

  function handleOverlay(e) {
    if (e.target === overlayRef.current) onClose();
  }
  // #endregion

  // #region Render
  return (
    <div
      className="checkout-overlay"
      ref={overlayRef}
      onMouseDown={handleOverlay}
      role="dialog"
      aria-modal="true"
    >
      <div className="checkout-modal" role="document">
        <form onSubmit={submit} className="checkout-form">
          <h3>Finalizar Venda</h3>
          <div className="itens-list">
            {cart.map((it, i) => (
              <div key={i} className="il-row">
                <span>
                  {it.nome} {it.quantidade > 1 && `(${it.quantidade}x)`}
                </span>
                <strong>{formatCurrency(it.preco_unit * it.quantidade)}</strong>
              </div>
            ))}
            <div className="il-total">
              <span>Total:</span>
              <strong>{formatCurrency(total)}</strong>
            </div>
          </div>
          <div className="form-grid">
            <div className="fg-item">
              <label>Cliente *</label>
              <select
                name="cliente_id"
                ref={firstRef}
                value={form.cliente_id}
                onChange={handleChange}
                required
              >
                <option value="">Selecione o cliente</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>
            <div className="fg-item">
              <label>Funcionário Responsável *</label>
              <select
                name="funcionario_id"
                value={form.funcionario_id}
                onChange={handleChange}
                required
              >
                <option value="">Selecione o funcionário</option>
                {funcionarios.map(f => (
                  <option key={f.id} value={f.id}>{f.nome}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="pay-methods">
            {['DINHEIRO', 'DEBITO', 'CREDITO', 'PIX'].map(m => (
              <button
                type="button"
                key={m}
                className={form.metodo_pagamento === m ? 'pm active' : 'pm'}
                onClick={() => setForm(p => ({ ...p, metodo_pagamento: m }))}
              >
                {m === 'DINHEIRO'
                  ? 'Dinheiro'
                  : m === 'DEBITO'
                    ? 'Cartão Débito'
                    : m === 'CREDITO'
                      ? 'Cartão Crédito'
                      : 'PIX'}
              </button>
            ))}
            <label className="fiado">
              <input
                type="checkbox"
                name="is_fiado"
                checked={form.is_fiado}
                onChange={handleChange}
              />{' '}
              Venda fiada
            </label>
          </div>
          <div className="fg-item">
            <label>Observações</label>
            <textarea
              name="observacoes"
              value={form.observacoes}
              onChange={handleChange}
              placeholder="Observações adicionais sobre a venda..."
            />
          </div>
          <div className="actions">
            <button type="button" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary">Finalizar Venda</button>
          </div>
        </form>
      </div>
    </div>
  );
  // #endregion
}
